import React, { useEffect, useState, useContext } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { useHistory, withRouter } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import TagsInput from './TagsInput'
import { mapErrorsMsg } from '../2020/utils'
import InputLimit, { LengthLimitTextarea } from './styles/InputLimit'
import SearchInput from './SearchInput'
import { Progress } from 'semantic-ui-react'
import Loading from './Loading'
import { client } from '../../index'
import { QUERY } from '../../graphql'
import cogoToast from 'cogo-toast'
import { disciplines as projectType } from '../../constant'
import { useTheme } from '../../context/2025/ThemeContext'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

const MAX_IMG_SIZE = 5 * 1024 * 1024
const MAX_VIDEO_SIZE = 50 * 1024 * 1024
const allowedImageExt = 'image/bmp, image/jpeg, image/png, image/gif'
const allowedVideoExt = 'video/*, .mkv, video/mp4,video/x-m4v'

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const grid = 8 // spacing for items

const getItemStyle = (isDragging, draggableStyle) => ({
  // basic styles
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  borderRadius: '3px',
  border: '1px solid lightgrey',

  // change background colour if dragging
  background: isDragging ? 'lightgreen' : 'white', // Changed color for visibility

  // styles we need to apply on draggables
  ...draggableStyle,
})

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'lightblue' : 'lightgrey', // Changed color for visibility
  padding: grid,
  width: '100%', // Adjust width as needed
})

const UpdateProject = (props) => {
  const { isDarkMode } = useTheme()
  const history = useHistory()
  const [project, setProject] = useState({
    id: '',
    title: '',
    primaryProjectType: '',
    secondaryProjectType: '',
    secondaryProjectType_2: '',
    contentType: { set: [] },
    contentData: { set: [] },
    coverImage: '',
    members: [],
    published: false,
  })
  const [memberInputs, setMemberInputs] = useState([])
  const [contentInputs, setContentInputs] = useState([])
  const [uploadFinished, setUploadFinished] = useState(true)
  const [loadingStates, setLoadingStates] = useState({})
  const [usersData, setUsersData] = useState([])
  const [error, setError] = useState(undefined)

  const projectId = props.location.search.split('id=')[1]

  const { data: userQuery } = useQuery(QUERY.USERS_QUERY, {
    variables: {
      where: {
        studentYear: 2025,
      },
    },
  })

  const { data, loading } = useQuery(SINGLE_PROJECT_QUERY, {
    variables: { id: projectId },
    fetchPolicy: 'no-cache',
  })

  const [uploadFile] = useMutation(USER_UPLOAD_FILE)

  useEffect(() => {
    try {
      if (!loading && data?.project && userQuery?.users) {
        if (Array.isArray(data.project.contentType)) {
          const _project = { ...data.project }
          _project.contentType = {
            set: data.project.contentType.map((type) => type),
          }
          _project.contentData = {
            set: data.project.contentData.map((contentData) => contentData),
          }
          _project.members = _project.members.map((member) => ({
            id: member.user.id,
            role: member.role,
          }))

          const _memberInputs = _project.members.map((member, i) => `${i + 1}`)
          setMemberInputs(_memberInputs)

          const existingInputs = _project.contentType.set.map((_, i) => `${i}`)

          setContentInputs(existingInputs)
          _project.tags = _project.tags > 0 ? _project.tags.split(',') : []

          setProject(_project)
        }
        const usersData = userQuery.users
        setUsersData(usersData)
      }
    } catch (error) {
      const err = mapErrorsMsg(error)
      setError(err)
    }
  }, [data, loading, userQuery])

  const appendMember = () => {
    var newMemberInput = `${memberInputs.length + 1}`
    setMemberInputs(memberInputs.concat([newMemberInput]))
    const studentProject = { ...project }
    studentProject.members = [...studentProject.members, { id: '', role: '' }]
    setProject(studentProject)
  }

  const appendContent = () => {
    const newContentInput = `${contentInputs.length}`
    const newContentInputs = contentInputs.concat([newContentInput])
    setContentInputs(newContentInputs)
    const _project = { ...project }
    _project.contentType.set.push('Text')
    _project.contentData.set.push('')
    setProject(_project)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setProject((prev) => ({ ...prev, [name]: value }))
  }

  const handleMemberSearch = (value, index) => {
    const _project = { ...project }
    const members = _project.members.map((mb, idx) => {
      if (index === idx) {
        return { ...mb, id: value }
      }
      return mb
    })
    setProject({ ...project, members })
  }

  const handleRole = (e, index) => {
    const { value } = e.target
    const _project = { ...project }
    const members = _project.members.map((mb, idx) => {
      if (idx === index) {
        return { ...mb, role: value }
      }
      return mb
    })
    setProject({ ...project, members })
  }

  const handleContentType = (e) => {
    const { name, value } = e.target
    const [, index] = name.split('contentType_')

    const _project = { ...project }

    _project.contentType.set.splice(parseInt(index), 1, value)
    _project.contentData.set.splice(parseInt(index), 1, '')

    setProject(_project)
  }

  const handleContentData = (e) => {
    const { name, value } = e.target
    const [, index] = name.split('contentData_')
    const _project = { ...project }

    _project.contentData.set.splice(parseInt(index), 1, value)

    setProject(_project)
  }

  const handleFileUpload = async (e) => {
    const files = e.target.files
    if (!files.length) return

    const file = files[0]
    if (file.size > MAX_IMG_SIZE) {
      alert('Maximum image size is 5MB')
      e.target.value = ''
      return
    }

    setUploadFinished(false)
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = async (event) => {
      if (reader.error) {
        console.error('FileReader error:', reader.error)
      } else if (event.total > MAX_IMG_SIZE) {
        alert('Maximum image size is 5MB')
        return
      } else {
        try {
          uploadFile({ variables: { file } }).then(({ data }) => {
            const coverImage = data.singleUpload.path
            setProject((prevState) => ({
              ...prevState,
              coverImage,
            }))
            setUploadFinished(true)
          })
        } catch (error) {
          console.error('Error uploading file:', error)
        }
        event.target.value = ''
      }

      reader.onerror = (error) => {
        console.error('Error reading file:', error)
        setUploadFinished(true)
      }
    }
  }

  const uploadContentFile = async (e) => {
    const { id, name } = e.target
    setUploadFinished(false)

    const files = e.target.files
    const file = files[0]
    const [media, index] = id.split('_')
    const isVideo = media === 'video'

    let interval
    const keyState = `contentType_${index}_loading`
    if (isVideo) {
      interval = fakeLoading(keyState)
    }
    const size = isVideo ? MAX_VIDEO_SIZE : MAX_IMG_SIZE
    if (file.size > size) {
      alert(`Maximum ${isVideo ? 'video' : 'image'} size is ${isVideo ? '50 MB' : '5 MB'}`)
      setUploadFinished(true)
      clearInterval(interval)
      e.target.value = null
      return
    } else {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = async (event) => {
        if (event.total > size) {
          alert(`Maximum ${isVideo ? 'video' : 'image'} size is ${isVideo ? '50 MB' : '5 MB'}`)
          setUploadFinished(true)
          clearInterval(interval)
          return
        }
        uploadFile({ variables: { file, isVideo } }).then(({ data }) => {
          clearInterval(interval)
          const path = data.singleUpload.path
          const [, index] = name.split('contentData_')
          const _contentType = isVideo ? 'Video' : 'Image'
          const _project = { ...project }
          if (_project.contentType.set.length <= parseInt(index)) {
            _project.contentType.set.push(_contentType)
            _project.contentData.set.push(path)
          } else {
            _project.contentType.set.splice(parseInt(index), 1, _contentType)
            _project.contentData.set.splice(parseInt(index), 1, path)
          }
          setProject(_project)
          setUploadFinished(true)
        })
      }
    }
  }

  const fakeLoading = (keyState) => {
    const percent = Math.ceil(Math.random() * 7) * 10
    const interval = setInterval(() => {
      setLoadingStates((prevStates) => {
        const currentPercent = prevStates[keyState] || 0
        return {
          ...prevStates,
          [keyState]: currentPercent < 90 ? currentPercent + percent : 10,
        }
      })
    }, 500)
    return interval
  }

  const onContentDragEnd = (result) => {
    // dropped outside the list or no movement
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }

    // --- IMPORTANT ---
    // We are dragging items *after* the description (index 0).
    // So, the source/destination indices from react-beautiful-dnd
    // correspond to indices in the *slice* of the arrays (from index 1 onwards).
    // We need to adjust these indices by adding 1 to apply them to the full arrays.
    const sourceIndexInFullArray = result.source.index + 1
    const destinationIndexInFullArray = result.destination.index + 1

    // Reorder both contentType and contentData arrays
    const reorderedContentTypes = reorder(
      project.contentType.set,
      sourceIndexInFullArray,
      destinationIndexInFullArray,
    )

    const reorderedContentData = reorder(
      project.contentData.set,
      sourceIndexInFullArray,
      destinationIndexInFullArray,
    )

    // Update the state
    setProject((prev) => ({
      ...prev,
      contentType: { set: reorderedContentTypes },
      contentData: { set: reorderedContentData },
    }))

    // Note: We don't need to update contentInputs, as it just tracks the *count*.
    // The visual order is now determined by the order in project.contentType.set / project.contentData.set
  }

  const updateProject = async (e) => {
    e.preventDefault()
    const tags = project.tags ? project.tags.join(',') : ''

    if (!project.title) {
      cogoToast.error('Project title is required')
      return
    }
    if (!project.coverImage) {
      cogoToast.error('Project cover image title is required')
      return
    }
    if (!project.primaryProjectType) {
      cogoToast.error('Project primary type is required')
      return
    }

    if (!project.contentData.set[0] || project.contentData.set[0].length === 0) {
      cogoToast.error('Project description is required')
      return
    }

    const filteredContentData = project.contentData.set.filter((cData) => cData !== '')

    const filteredContentType = project.contentType.set.filter(
      (cType, index) => index < filteredContentData.length,
    )

    const variables = {
      id: projectId,
      title: project.title,
      tags,
      primaryProjectType: project.primaryProjectType,
      secondaryProjectType: project.secondaryProjectType,
      secondaryProjectType_2: project.secondaryProjectType_2,
      contentType: filteredContentType,
      contentData: filteredContentData,
      coverImage: project.coverImage,
      members: project.members,
    }

    const res = await client.mutate({
      mutation: UPDATE_PROJECT_MUTATION,
      variables,
    })
    const newProject = {
      ...project,
      members: res.data.updateProject.members.map((mb) => {
        const { role, user } = mb
        return { id: user.id, role }
      }),
    }

    setProject(newProject)
    history.push({
      pathname: '/project',
      search: `?id=${res.data.updateProject.id}`,
    })
  }

  const handlePreview = async () => {
    const tags = project.tags ? project.tags.join(',') : ''

    if (!project.title) {
      cogoToast.error('Project title is required')
      return
    }
    if (!project.coverImage) {
      cogoToast.error('Project cover image title is required')
      return
    }
    if (!project.primaryProjectType) {
      cogoToast.error('Project primary type is required')
      return
    }

    if (!project.contentData.set[0] || project.contentData.set[0].length === 0) {
      cogoToast.error('Project description is required')
      return
    }

    const filteredContentData = project.contentData.set.filter((cData) => cData !== '')

    const filteredContentType = project.contentType.set.filter(
      (cType, index) => index < filteredContentData.length,
    )

    const variables = {
      id: projectId,
      title: project.title,
      tags,
      primaryProjectType: project.primaryProjectType,
      secondaryProjectType: project.secondaryProjectType,
      secondaryProjectType_2: project.secondaryProjectType_2,
      contentType: filteredContentType,
      contentData: filteredContentData,
      coverImage: project.coverImage,
      members: project.members,
    }

    try {
      await client.mutate({
        mutation: UPDATE_PROJECT_MUTATION,
        variables,
      })
    } catch (error) {
      console.log('error: ', error)
    }
    history.push({
      pathname: '/preview',
      search: `?id=${projectId}`,
    })
    return true
  }

  const handleRemoveUserFromProject = async (member, index) => {
    const _project = { ...project }
    const members = _project.members.filter((_, idx) => idx !== index)
    setProject({ ...project, members })
    const _memberInputs = [...memberInputs]
    _memberInputs.splice(index, 1)
    setMemberInputs(_memberInputs)
  }

  const handleRemoveContent = (index) => {
    const _project = { ...project }
    const setType = _project.contentType.set.filter((type, idx) => idx !== index)
    const setData = _project.contentData.set.filter((type, idx) => idx !== index)
    setProject({
      ...project,
      contentType: { set: setType },
      contentData: { set: setData },
    })
  }

  const handleChangeTags = (tags) => {
    setProject({
      ...project,
      tags,
    })
  }

  const handleDescription = (e) => {
    const { name, value } = e.target
    const [, index] = name.split('contentData_')
    const _project = { ...project }
    _project.contentData.set.splice(0, 1, value)
    setProject(_project)
  }

  if (loading) return <Loading />

  if (error) {
    return <div>{error}</div>
  }

  return (
    <ProjectForm onSubmit={(e) => updateProject(e)}>
      <fieldset disabled={loading} aria-busy={loading}>
        <GridOne>
          <Grid>
            <div>
              <HelveticaTitle>Project Information</HelveticaTitle>
              <label htmlFor='title'>
                Project Title
                <InputLimit
                  type='text'
                  id='title'
                  name='title'
                  placeholder='Title'
                  required
                  maxLength={55}
                  value={project.title}
                  onChange={(e) => handleChange(e)}
                  fromtop={44}
                />
              </label>
              {project.members.map((member, index) => {
                const { role, id } = member
                const user = usersData.find((usr) => usr.id === id)
                return (
                  <Columns key={`key_${index + 1}`}>
                    <label htmlFor={`member_${index + 1}`}>
                      Member
                      <SearchInput
                        users={usersData.map(
                          (usr) => !project.members.some((mb) => mb.id === usr.id) && usr,
                        )}
                        onChange={(value) => handleMemberSearch(value, index)}
                        value={user && `${user?.firstName} ${user?.lastName}`}
                        disabled={index === 0}
                      />
                      {index !== 0 && (
                        <RemoveText onClick={() => handleRemoveUserFromProject(member, index)}>
                          Remove
                        </RemoveText>
                      )}
                    </label>
                    <label htmlFor={`role_${index + 1}`}>
                      Role
                      <input
                        type='text'
                        defaultValue={role}
                        onChange={(e) => handleRole(e, index)}
                      />
                    </label>
                  </Columns>
                )
              })}
              <AddText onClick={() => appendMember()}>+ Group Member</AddText>
            </div>
            <div>
              <label htmlFor='coverImage'>
                Cover Image
                <UploadBlock>
                  <input
                    type='file'
                    id='coverImage'
                    name='coverImage'
                    placeholder='Upload an image'
                    onChange={(e) => handleFileUpload(e)}
                    className='upload-input'
                    accept={allowedImageExt}
                  />
                  {!project.coverImage ? (
                    <img className='upload-placeholder' src={'/static/drawing.svg'} alt='' />
                  ) : (
                    <CoverImage src={project.coverImage} alt='Upload Preview' />
                  )}
                  <UploadIcon src='/static/edit.svg' alt='Change Image' />
                </UploadBlock>
              </label>
            </div>
            <div>
              <label htmlFor='tags'>
                <HelveticaTitle>Tags</HelveticaTitle>
                <TagsInput defaultValue={project.tags || []} onChange={handleChangeTags} />
              </label>
            </div>
          </Grid>
          <GridThree>
            <label htmlFor='primaryProjectType'>
              <HelveticaTitle>Primary Project Type</HelveticaTitle>
              <select
                id='primaryProjectType'
                name='primaryProjectType'
                value={project.primaryProjectType}
                onChange={handleChange}
                required
              >
                <option disabled value={''}>
                  -- select a project type --
                </option>
                {Object.keys(projectType).map((key) => (
                  <option key={key} value={key}>
                    {projectType[key]}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor='secondaryProjectType'>
              <HelveticaTitle>Secondary Project Type</HelveticaTitle>
              <select
                id='secondaryProjectType'
                name='secondaryProjectType'
                value={project?.secondaryProjectType}
                onChange={handleChange}
              >
                <option disabled value={''}>
                  -- select a project type --
                </option>
                {Object.keys(projectType).map((key) => (
                  <option key={key} value={key}>
                    {projectType[key]}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor='secondaryProjectType_2'>
              <HelveticaTitle>Secondary Project Type</HelveticaTitle>
              <select
                id='secondaryProjectType_2'
                name='secondaryProjectType_2'
                value={project?.secondaryProjectType_2}
                onChange={handleChange}
              >
                <option disabled value={''}>
                  -- select a project type --
                </option>
                {Object.keys(projectType).map((key) => (
                  <option key={key} value={key}>
                    {projectType[key]}
                  </option>
                ))}
              </select>
            </label>
          </GridThree>
          <label htmlFor={`contentData_0`}>
            <HelveticaTitle>Short Description</HelveticaTitle>
            <LengthLimitTextarea
              type='text'
              id={`contentData_0`}
              name={`contentData_0`}
              maxLength={400}
              placeholder=''
              defaultValue={project?.contentData?.set[0]}
              onChange={(e) => handleDescription(e)}
            />
          </label>
          <HelveticaTitle>Content</HelveticaTitle>
          <h2 className='text-[2rem] mb-4'>
            Add text, headings, images, or videos to showcase your project. <br/>Drag and drop to reorder
            content blocks.
          </h2>

          {/* Wrap the draggable area with DragDropContext */}
          <DragDropContext onDragEnd={onContentDragEnd}>
            <Droppable droppableId='projectContent'>
              {(provided, snapshot) => (
                <div // This div is the droppable area
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={getListStyle(snapshot.isDraggingOver)}
                >
                  {/* Map over content starting from index 1 (after description) */}
                  {project.contentType?.set?.slice(1).map((contentType, index) => {
                    // Calculate the actual index in the full contentType/contentData arrays
                    const actualIndex = index + 1
                    // Get the corresponding data for this block
                    const contentData = project.contentData.set[actualIndex]
                    // Create a unique and stable ID for the draggable item
                    const draggableId = `content-${actualIndex}`
                    // Check if this specific block is loading (e.g., file upload)
                    const isLoading = loadingStates[`contentType_${actualIndex}_loading`] // Use the correct loading state key

                    return (
                      // Wrap each content block with Draggable
                      <Draggable key={draggableId} draggableId={draggableId} index={index}>
                        {(provided, snapshot) => (
                          <ContentBlockWrapper // This is the draggable item container
                            ref={provided.innerRef}
                            {...provided.draggableProps} // Apply draggable props here
                            // Do NOT apply dragHandleProps here if using a separate handle
                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                            isDarkMode={isDarkMode} // Pass theme prop if needed by styled-component
                          >
                            {/* Drag Handle Element */}
                            <div className='flex flex-row justify-between'>
                              <DragHandle {...provided.dragHandleProps}>
                                {' '}
                                {/* Apply dragHandleProps HERE */}
                                <DragIcon src='/static/drag-handle.svg' alt='drag handle' />
                                <span>Block {index + 1}</span> {/* Simple Label */}
                              </DragHandle>
                              <RemoveLink onClick={() => handleRemoveContent(actualIndex)}>
                                Remove Block
                              </RemoveLink>
                            </div>

                            {/* Content Block Rendering Logic (similar to your original) */}
                            <GridOne key={`contentKey_${actualIndex}`}>
                              {' '}
                              {/* Use actualIndex for keys */}
                              {/* Controls for Type Selection and Removal */}
                              <ContentControls>
                                <label htmlFor={`contentType_${actualIndex}`}>
                                  Type:
                                  <select
                                    id={`contentType_${actualIndex}`}
                                    name={`contentType_${actualIndex}`}
                                    onChange={handleContentType}
                                    value={contentType} // Controlled component
                                  >
                                    {/* Removed the disabled option to ensure a type is always selected */}
                                    <option value='Heading'>Heading</option>
                                    <option value='Text'>Text</option>
                                    <option value='Image'>Image</option>
                                    <option value='Video'>Video</option>
                                  </select>
                                </label>
                                {/* Pass actualIndex to handleRemoveContent */}
                              </ContentControls>
                              {/* Input fields based on selected contentType */}
                              {contentType === 'Heading' && (
                                <label htmlFor={`contentData_${actualIndex}`}>
                                  Heading Text
                                  <ContentInput
                                    type='text'
                                    id={`contentData_${actualIndex}`}
                                    name={`contentData_${actualIndex}`}
                                    placeholder='Enter heading'
                                    value={contentData || ''} // Controlled
                                    onChange={handleContentData}
                                    required
                                  />
                                </label>
                              )}
                              {contentType === 'Text' && (
                                <label htmlFor={`contentData_${actualIndex}`}>
                                  Paragraph Text
                                  <ContentTextarea
                                    id={`contentData_${actualIndex}`}
                                    name={`contentData_${actualIndex}`}
                                    placeholder='Enter text'
                                    value={contentData || ''} // Controlled
                                    onChange={handleContentData}
                                    rows={5}
                                    required
                                  />
                                </label>
                              )}
                              {contentType === 'Image' && (
                                <label htmlFor={`image_${actualIndex}`}>
                                  Image File
                                  <ContentInput
                                    type='file'
                                    id={`image_${actualIndex}`} // Match ID used in uploadContentFile
                                    name={`contentData_${actualIndex}`} // Name links to state array index
                                    onChange={uploadContentFile}
                                    accept={allowedImageExt}
                                  />
                                  {/* Display Preview if contentData exists */}
                                  {contentData && (
                                    <img
                                      src={contentData}
                                      alt={`Content ${actualIndex} preview`}
                                      style={{
                                        maxWidth: '200px',
                                        height: 'auto',
                                        marginTop: '10px',
                                      }}
                                    />
                                  )}
                                  {/* Progress Bar - Ensure loadingStates key is correct */}
                                  {isLoading !== undefined && (
                                    <Progress percent={isLoading} indicating size='small' />
                                  )}
                                </label>
                              )}
                              {contentType === 'Video' && (
                                <label htmlFor={`video_${actualIndex}`}>
                                  Video File
                                  <ContentInput
                                    type='file'
                                    id={`video_${actualIndex}`} // Match ID used in uploadContentFile
                                    name={`contentData_${actualIndex}`} // Name links to state array index
                                    onChange={uploadContentFile}
                                    accept={allowedVideoExt}
                                  />
                                  {/* Display Preview if contentData exists */}
                                  {contentData && (
                                    <video
                                      controls
                                      style={{
                                        maxWidth: '300px',
                                        height: 'auto',
                                        marginTop: '10px',
                                      }}
                                    >
                                      <source src={contentData} type='video/mp4' />{' '}
                                      {/* Assuming mp4, adjust if needed */}
                                      Your browser does not support the video tag.
                                    </video>
                                  )}
                                  {/* Progress Bar - Ensure loadingStates key is correct */}
                                  {isLoading !== undefined && (
                                    <Progress percent={isLoading} indicating size='small' />
                                  )}
                                </label>
                              )}
                            </GridOne>
                          </ContentBlockWrapper>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder} {/* Don't forget the placeholder for smooth dragging */}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {/* <HelveticaTitle>Content</HelveticaTitle>
          {contentInputs &&
            contentInputs?.length > 1 &&
            contentInputs?.slice(1).map((input) => (
              <GridOne key={`contentKey_${input}`}>
                <label htmlFor={`contentType_${input}`}>
                  <select
                    id={`contentType_${input}`}
                    name={`contentType_${input}`}
                    onChange={(e) => handleContentType(e)}
                    defaultValue={project?.contentType?.set[parseInt(input)]}
                    style={{ width: '24%' }}
                  >
                    <option disabled value={'DEFAULT'}>
                      -- select a content type --
                    </option>
                    <option value='Text'>Text</option>
                    <option value='Heading'>Heading</option>
                    <option value='Video'>Video</option>
                    <option value='Image'>Image</option>
                  </select>
                </label>
                {project.contentType.set[parseInt(input)] === 'Heading' && (
                  <label htmlFor={`contentData_${input}`}>
                    Heading
                    <ContentInput
                      type='text'
                      id={`contentData_${input}`}
                      name={`contentData_${input}`}
                      placeholder=''
                      defaultValue={project.contentData.set[parseInt(input)]}
                      onChange={(e) => handleContentData(e)}
                    />
                    <RemoveText onClick={() => handleRemoveContent(parseInt(input))}>
                      Remove
                    </RemoveText>
                  </label>
                )}
                {project.contentType.set[parseInt(input)] === 'Text' && (
                  <label htmlFor={`contentData_${input}`}>
                    Text
                    <ContentTextarea
                      type='text'
                      id={`contentData_${input}`}
                      name={`contentData_${input}`}
                      placeholder=''
                      defaultValue={project.contentData.set[parseInt(input)]}
                      onChange={(e) => handleContentData(e)}
                    />
                    <RemoveText onClick={() => handleRemoveContent(parseInt(input))}>
                      Remove
                    </RemoveText>
                  </label>
                )}
                {project.contentType.set[parseInt(input)] === 'Video' && (
                  <label htmlFor={`contentData_${input}`}>
                    Video
                    <ContentInput
                      type='file'
                      id={`video_${input}`}
                      name={`contentData_${input}`}
                      placeholder='Upload a video'
                      onChange={uploadContentFile}
                      accept={allowedVideoExt}
                    />
                    {[`contentType_${input}_loading`] > 0 && (
                      <Progress
                        percent={[`contentType_${input}_loading`]}
                        size='small'
                        color='green'
                      />
                    )}
                    <RemoveText onClick={() => handleRemoveContent(parseInt(input))}>
                      Remove
                    </RemoveText>
                  </label>
                )}
                {project.contentType.set[parseInt(input)] === 'Image' && (
                  <label htmlFor={`contentData_${input}`}>
                    Image
                    <ContentInput
                      type='file'
                      id={`image_${input}`}
                      name={`contentData_${input}`}
                      placeholder='Upload an image'
                      onChange={uploadContentFile}
                      accept={allowedImageExt}
                    />
                    {[`contentType_${input}_loading`] > 0 && (
                      <Progress
                        percent={[`contentType_${input}_loading`]}
                        size='small'
                        color='green'
                      />
                    )}
                    <RemoveText onClick={() => handleRemoveContent(parseInt(input))}>
                      Remove
                    </RemoveText>
                  </label>
                )}
                {project.contentData.set.map((content, j) => {
                  if (content !== undefined && parseInt(j) === parseInt(input)) {
                    if (project.contentType.set[parseInt(input)] === 'Image') {
                      return <img src={content} alt='Upload Preview ' key={content} />
                    } else if (project?.contentType?.set[parseInt(input)] === 'Video') {
                      return (
                        <video key={j}>
                          <source type='video/mp4' src={content} key={content}></source>
                        </video>
                      )
                    }
                  }
                  return null
                })}
              </GridOne>
            ))} */}
          <AddText onClick={() => appendContent()}>+ Add Field</AddText>
          <GridButton>
            <div></div>
            <Button type='Button' onClick={handlePreview} isLoading={!uploadFinished}>
              SAVE/PREVIEW
            </Button>
            <Button type='submit' isLoading={!uploadFinished}>
              PUBLISH
            </Button>
          </GridButton>
        </GridOne>
      </fieldset>
    </ProjectForm>
  )
}

export default withRouter(UpdateProject)

const SINGLE_PROJECT_QUERY = gql`
  query SINGLE_PROJECT_QUERY($id: ID!) {
    project(where: { id: $id }) {
      id
      title
      tags
      primaryProjectType
      secondaryProjectType
      secondaryProjectType_2
      contentType
      contentData
      coverImage
      members {
        id
        role
        user {
          id
        }
      }
      published
    }
  }
`

export const UPDATE_PROJECT_MUTATION = gql`
  mutation UPDATE_PROJECT_MUTATION(
    $id: ID!
    $title: String
    $tags: String
    $primaryProjectType: String
    $secondaryProjectType: String
    $secondaryProjectType_2: String
    $contentType: [ContentType]
    $contentData: [String]
    $coverImage: String
    $members: [CustomInput]
    $published: Boolean
  ) {
    updateProject(
      id: $id
      title: $title
      tags: $tags
      primaryProjectType: $primaryProjectType
      secondaryProjectType: $secondaryProjectType
      secondaryProjectType_2: $secondaryProjectType_2
      contentType: $contentType
      contentData: $contentData
      members: $members
      coverImage: $coverImage
      published: $published
    ) {
      id
      title
      tags
      primaryProjectType
      secondaryProjectType
      secondaryProjectType_2
      contentType
      contentData
      members {
        id
        role
        user {
          id
        }
      }
      coverImage
      published
    }
  }
`

const USER_UPLOAD_FILE = gql`
  mutation ($file: Upload!, $isVideo: Boolean) {
    singleUpload(file: $file, isVideo: $isVideo) {
      id
      path
      filename
    }
  }
`

// ****** Define Styled Components (Add/Modify as needed) ******
// Add these styled component definitions to your UpdateProject.js file

const DragHandle = styled.div`
  /* Make it easy to grab */
  cursor: grab;
  /* Layout: Use flex to align icon and text nicely */
  display: inline-flex; /* Or 'flex' if you want it block-level */
  align-items: center; /* Vertically center icon and text */
  /* Visual cue */
  padding: 6px 8px; /* Adjust padding as needed */
  margin-bottom: 10px; /* Space below the handle */
  background-color: ${(props) => (props.isDarkMode ? '#444' : '#eee')}; /* Themed background */
  border: 1px solid ${(props) => (props.isDarkMode ? '#555' : '#ddd')}; /* Subtle border */
  border-radius: 4px; /* Slightly rounded corners */
  color: ${(props) => (props.isDarkMode ? '#eee' : '#333')}; /* Text color */
  font-size: 0.9em; /* Slightly smaller text */
  transition: background-color 0.2s ease;

  /* Indicate when the handle is actively being dragged (optional, relies on parent styling) */
  &:active {
    cursor: grabbing;
    background-color: ${(props) => (props.isDarkMode ? '#555' : '#ddd')};
  }

  /* Style the text next to the icon if you have it */
  span {
    margin-left: 8px; /* Space between icon and text */
  }
`

const DragIcon = styled.img`
  /* Control the icon's size */
  width: 18px; /* Adjust size as needed */
  height: 18px;
  /* Prevents the image from affecting line height if inline */
  display: block;
  /* You might want to invert colors for dark mode if using a simple SVG */
  filter: ${(props) => (props.isDarkMode ? 'invert(90%)' : 'none')};
`

// You might also want styled-components for these helpers if not already defined:
// (Copied from previous answer for completeness)

const ContentBlockWrapper = styled.div`
  border: 1px dashed ${(props) => (props.isDarkMode ? '#555' : '#ccc')};
  margin-bottom: 15px;
  padding: 15px;
  background-color: ${(props) => (props.isDarkMode ? '#2a2a2a' : '#f9f9f9')};
  position: relative; /* Needed for absolute positioning if required */
  border-radius: 4px;
`

const ContentControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px; /* Increased margin */
  padding-bottom: 15px; /* Increased padding */
  border-bottom: 1px solid ${(props) => (props.isDarkMode ? '#444' : '#eee')};

  label {
    margin-bottom: 0 !important; /* Override default label margin */
    display: inline-flex !important; /* Align label text and select */
    align-items: center;
    font-size: 0.95em; /* Slightly smaller text */
  }

  select {
    max-width: 150px; /* Limit width of the select dropdown */
    margin-left: 8px; /* Add some space */
    height: auto !important; /* Override global style if needed */
    padding: 5px 10px !important; /* Adjust padding */
    font-size: 0.95em !important; /* Adjust font size */
  }
`

const RemoveLink = styled.button`
  // Changed to button for better accessibility
  font-size: 1.4rem; // Adjusted size
  font-family: 'grotesk-regular', sans-serif; // Match form styles
  color: #cc0000; // Red color for removal
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  padding: 5px; // Add padding for easier clicking
  margin: 0; // Reset margin
  width: auto; // Auto width for button
  &:hover {
    color: #ff0000; // Brighter red on hover
  }
`

const ProjectForm = styled.form`
  padding: 20px !important;
  font-size: 1.5rem !important;
  line-height: 1.5 !important;
  font-weight: normal !important;
  margin: 50px 5vw 0 !important;
  font-family: 'grotesk-regular' !important;
  color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')} !important;

  label {
    color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')} !important;
    display: block !important;
    margin-bottom: 1rem !important;
  }

  input,
  textarea,
  select {
    height: 50px !important;
    width: 100% !important;
    padding: 0.5rem 1.5rem !important;
    font-size: 1.6rem !important;
    // font-family: 'grotesk-regular' !important;
    color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')} !important;
    background: ${(props) => (props.isDarkMode ? '#222222' : '#ffffff')} !important;
    border: 1px solid ${(props) => (props.isDarkMode ? '#444444' : '#dddddd')} !important;
    border-radius: 4px !important;

    &::placeholder {
      color: ${(props) => (props.isDarkMode ? '#aaaaaa' : '#999999')} !important;
      opacity: 1 !important;
    }

    &:focus {
      outline: 0 !important;
      border-color: ${(props) =>
        props.isDarkMode ? 'var(--theme-lime)' : 'var(--theme-lavender)'} !important;
    }
  }

  textarea {
    height: 200px !important;
  }

  button,
  input[type='submit'] {

    background: ${(props) =>
      props.isDarkMode ? 'var(--theme-lime)' : 'var(--theme-lavender)'} !important;
    color: ${(props) =>
      props.isDarkMode ? 'var(--theme-black)' : 'var(--theme-black)'} !important;
    border: 0 !important;
    // font-family: 'grotesk-regular' !important;
    font-size: 1.6rem !important;
    padding: 1.2rem 2.4rem !important;
    // margin-top: 3rem !important;
    border-radius: 3rem !important;
    transition: background-color 0.3s ease, color 0.3s ease;

    &:hover {
      background: ${(props) =>
        props.isDarkMode ? 'var(--theme-lavender)' : 'var(--theme-lime)'} !important;
    }
  }

  fieldset {
    border: 0 !important;
    padding: 0 !important;
  }
`

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-gap: 20px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const GridOne = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 50px;
  margin-top: 50px;

  video {
    max-width: 50%;
  }
`

const GridThree = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1.5fr 0.5fr;
  grid-gap: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const GridButton = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr;
  grid-gap: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-gap: 20px;
  }
`

const UploadBlock = styled.div`
  background-color: ${(props) => (props.isDarkMode ? '#333333' : '#e3e3e3')};
  position: relative;
  display: block;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  overflow: hidden;
  border-radius: 4px;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
  }

  .upload-input {
    position: relative;
    z-index: 2;
    width: 100%;
    height: calc(21.5em + 6.75rem + 2px);
    margin: 0;
    opacity: 0;
  }

  .upload-placeholder {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    opacity: 0.5;
    z-index: 2;
  }
`

const UploadIcon = styled.img`
  background: ${(props) => (props.isDarkMode ? '#444444' : 'rgb(243, 215, 222)')};
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.25);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
  height: 50px;
  width: 50px;
  padding: 10px;

  &:hover,
  &:focus {
    outline: none;
  }
`

const CoverImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`

const HelveticaTitle = styled.p`
  font-family: 'grotesk-bold', sans-serif;
  color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')};
  font-size: 2.4rem;
  margin-bottom: 2rem;
`

const Button = styled.button`
  ${(props) =>
    props.isLoading
      ? `
    opacity: .7;
    pointer-events: none;`
      : ''}
`

const AddText = styled.p`
  font-size: 1.8rem;
  color: ${(props) => (props.isDarkMode ? 'var(--theme-lime)' : 'var(--theme-lavender)')};
  text-decoration: underline;
  cursor: pointer;
  margin: 2rem 0;
`

const RemoveText = styled.p`
  font-size: 1.8rem;
  color: ${(props) => (props.isDarkMode ? 'var(--theme-lime)' : 'var(--theme-lavender)')};
  text-decoration: underline;
  cursor: pointer;
  margin-top: 1rem;
`

const ContentInput = styled.input`
  margin-bottom: 30px;
  width: 100%;
`

const ContentTextarea = styled.textarea`
  margin-bottom: 30px;
  width: 100%;
`
